/**
 * Availability Calendar Component
 * Displays staff availability calendar with assignments, deadlines, and capacity
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState } from '@tracekit/core';
import { getAPIClient } from '@tracekit/core';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info,
  Filter,
  User,
  MapPin,
  Building2,
  Award,
} from 'lucide-react';

interface StaffMember {
  id: string;
  name: string;
  department?: string;
  designations?: string[];
  availability?: string;
}

interface Assignment {
  team_composition_id: string;
  project_type?: string;
  service_line?: string;
  role?: string;
  created_at?: string;
}

interface Deadline {
  opportunity_id: string;
  title: string;
  deadline: string;
  days_until: number;
  status?: string;
}

interface AvailabilityData {
  staff_member_id: string;
  staff_name: string;
  current_assignments: Assignment[];
  upcoming_deadlines: Deadline[];
  default_capacity: {
    hours_per_week: number;
    fte: number;
  };
}

interface CapacityData {
  staff_member_id: string;
  staff_name: string;
  current_projects: Assignment[];
  upcoming_deadlines: Deadline[];
  proposal_assignments: number;
  capacity: {
    total_hours_per_week: number;
    total_fte: number;
    assigned_hours_per_week: number;
    available_hours_per_week: number;
    available_fte: number;
    utilization_percent: number;
  };
  warnings: {
    capacity_warning: boolean;
    capacity_exceeded: boolean;
  };
}

interface AvailabilityCalendarProps {
  projectId: string;
  staffMembers: StaffMember[];
  selectedStaffId?: string;
  onStaffSelect?: (staffId: string) => void;
}

type ViewMode = 'month' | 'week' | 'day';
type DateRange = '3months' | '6months' | '12months';

export function AvailabilityCalendar({
  projectId,
  staffMembers,
  selectedStaffId,
  onStaffSelect,
}: AvailabilityCalendarProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [dateRange, setDateRange] = useState<DateRange>('6months');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState<string | undefined>(selectedStaffId);
  const [filters, setFilters] = useState({
    department: 'all',
    designation: 'all',
    availability: 'all',
  });

  // Calculate date range based on selection
  const dateRangeDates = useMemo(() => {
    const start = new Date(currentDate);
    start.setMonth(start.getMonth() - 1); // Show previous month too
    
    const end = new Date(currentDate);
    const months = dateRange === '3months' ? 3 : dateRange === '6months' ? 6 : 12;
    end.setMonth(end.getMonth() + months);
    
    return { start, end };
  }, [currentDate, dateRange]);

  // Fetch availability data for selected staff
  const { data: availabilityData, isLoading: availabilityLoading } = useQuery<AvailabilityData>({
    queryKey: ['staff-availability', projectId, selectedStaff],
    queryFn: async () => {
      if (!selectedStaff || !projectId) return null;
      const api = getAPIClient();
      const response = await api.get(`/api/rfp/staff/availability/${selectedStaff}`, {
        params: { project_id: projectId },
      });
      return response;
    },
    enabled: !!selectedStaff && !!projectId,
  });

  // Fetch capacity data for selected staff
  const { data: capacityData, isLoading: capacityLoading } = useQuery<CapacityData>({
    queryKey: ['staff-capacity', projectId, selectedStaff],
    queryFn: async () => {
      if (!selectedStaff || !projectId) return null;
      const api = getAPIClient();
      const response = await api.get(`/api/rfp/staff/capacity/${selectedStaff}`, {
        params: { project_id: projectId },
      });
      return response;
    },
    enabled: !!selectedStaff && !!projectId,
  });

  // Filter staff members
  const filteredStaff = useMemo(() => {
    return staffMembers.filter((staff) => {
      if (filters.department !== 'all' && staff.department !== filters.department) return false;
      if (filters.designation !== 'all') {
        const designations = staff.designations || [];
        if (!designations.includes(filters.designation)) return false;
      }
      if (filters.availability !== 'all' && staff.availability !== filters.availability) return false;
      return true;
    });
  }, [staffMembers, filters]);

  // Handle staff selection
  const handleStaffSelect = (staffId: string) => {
    setSelectedStaff(staffId);
    onStaffSelect?.(staffId);
  };

  // Navigation
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  // Get unique departments and designations for filters
  const departments = useMemo(() => {
    const depts = new Set(staffMembers.map(s => s.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [staffMembers]);

  const designations = useMemo(() => {
    const desigs = new Set(staffMembers.flatMap(s => s.designations || []));
    return Array.from(desigs).sort();
  }, [staffMembers]);

  return (
    <div className="space-y-6">
      {/* Filters and Controls */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="3months">Next 3 Months</option>
              <option value="6months">Next 6 Months</option>
              <option value="12months">Next 12 Months</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Department</label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Designation</label>
            <select
              value={filters.designation}
              onChange={(e) => setFilters({ ...filters, designation: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Designations</option>
              {designations.map(desig => (
                <option key={desig} value={desig}>{desig}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Staff List - 20% width */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Staff Members ({filteredStaff.length})
            </h3>
            {filteredStaff.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No staff members found</p>
                <p className="text-xs mt-1">Check filters or ensure staff data is loaded</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredStaff.map((staff) => (
                <button
                  key={staff.id}
                  onClick={() => handleStaffSelect(staff.id)}
                  className={`w-full text-left p-2 rounded-lg border transition-colors ${
                    selectedStaff === staff.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900 dark:text-theme-text">
                    {staff.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {staff.department && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {staff.department}
                      </span>
                    )}
                    {staff.designations && staff.designations.length > 0 && (
                      <span className="flex items-center gap-1 mt-1">
                        <Award className="w-3 h-3" />
                        {staff.designations.slice(0, 2).join(', ')}
                        {staff.designations.length > 2 && ` +${staff.designations.length - 2}`}
                      </span>
                    )}
                  </div>
                </button>
              ))}
              </div>
            )}
          </Card>
        </div>

        {/* Calendar and Details - 80% width */}
        <div className="lg:col-span-4 space-y-6">
          {!selectedStaff ? (
            <Card className="p-12">
              <EmptyState
                icon={<User className="w-12 h-12" />}
                title="Select a Staff Member"
                description="Choose a staff member from the list to view their availability calendar and capacity assessment."
              />
            </Card>
          ) : (
            <>
              {/* Calendar View */}
              <Card className="p-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateDate('prev')}
                      className="h-9 w-9 p-0 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-300 dark:border-gray-600"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="font-bold text-xl text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
                      {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateDate('next')}
                      className="h-9 w-9 p-0 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-300 dark:border-gray-600"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentDate(new Date())}
                    className="px-4 py-2 rounded-lg font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Today
                  </Button>
                </div>

                {/* Calendar Grid - Explicit Grid Layout */}
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                  <style>{`
                    .calendar-month-grid {
                      display: grid !important;
                      grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
                      width: 100% !important;
                    }
                    .calendar-day-header {
                      display: block !important;
                      width: 100% !important;
                      box-sizing: border-box !important;
                    }
                    .calendar-day-cell {
                      display: block !important;
                      width: 100% !important;
                      box-sizing: border-box !important;
                    }
                  `}</style>
                  
                  {/* Day headers row */}
                  <div 
                    className="calendar-month-grid bg-gray-50 dark:bg-gray-800 border-b-2 border-gray-300 dark:border-gray-600"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}
                  >
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                      <div 
                        key={day} 
                        className={`calendar-day-header p-3 text-center text-sm font-bold text-gray-700 dark:text-gray-300 ${
                          idx < 6 ? 'border-r border-gray-300 dark:border-gray-600' : ''
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar days grid */}
                  <div 
                    className="calendar-month-grid"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}
                  >
                    {(() => {
                      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                      const startDate = new Date(firstDayOfMonth);
                      startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
                      
                      const days = [];
                      for (let i = 0; i < 42; i++) {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + i);
                        days.push(date);
                      }
                      
                      return days.map((date, idx) => {
                        const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        
                        // Get assignments for this date
                        const dateAssignments = availabilityData?.current_assignments.filter(a => {
                          if (!a.created_at) return false;
                          const assignDate = new Date(a.created_at);
                          return assignDate.toDateString() === date.toDateString();
                        }) || [];
                        
                        const dateDeadlines = availabilityData?.upcoming_deadlines.filter(d => {
                          const deadlineDate = new Date(d.deadline);
                          return deadlineDate.toDateString() === date.toDateString();
                        }) || [];
                        
                        const totalEvents = dateAssignments.length + dateDeadlines.length;
                        
                        return (
                          <div
                            key={idx}
                            className={`calendar-day-cell min-h-[100px] p-2 border-r border-b border-gray-200 dark:border-gray-700 flex flex-col ${
                              !isCurrentMonth 
                                ? 'bg-gray-50 dark:bg-gray-900/50 text-gray-400 dark:text-gray-600' 
                                : isWeekend
                                ? 'bg-gray-50/50 dark:bg-gray-800/30'
                                : 'bg-white dark:bg-gray-900'
                            } ${
                              isToday 
                                ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                                : ''
                            } hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
                          >
                            {/* Date number */}
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-semibold ${
                                isCurrentMonth 
                                  ? isToday
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-900 dark:text-gray-100'
                                  : 'text-gray-400 dark:text-gray-600'
                              }`}>
                                {date.getDate()}
                              </span>
                              {isToday && (
                                <span className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></span>
                              )}
                            </div>
                            
                            {/* Events container */}
                            <div className="flex-1 space-y-1 overflow-y-auto">
                              {dateAssignments.slice(0, 2).map((assignment, aIdx) => (
                                <div 
                                  key={aIdx}
                                  className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 rounded px-1.5 py-0.5 font-medium truncate border border-emerald-200 dark:border-emerald-800"
                                  title={assignment.project_type || assignment.service_line || 'Assignment'}
                                >
                                  {assignment.project_type || 'Assignment'}
                                </div>
                              ))}
                              {dateDeadlines.slice(0, 2).map((deadline, dIdx) => (
                                <div 
                                  key={dIdx}
                                  className="text-[10px] bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 rounded px-1.5 py-0.5 font-medium truncate border border-rose-200 dark:border-rose-800"
                                  title={deadline.title}
                                >
                                  {deadline.title.length > 12 ? deadline.title.substring(0, 12) + '...' : deadline.title}
                                </div>
                              ))}
                              {totalEvents > 4 && (
                                <div className="text-[9px] text-gray-500 dark:text-gray-400 font-medium px-1">
                                  +{totalEvents - 4} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </Card>

              {/* Capacity Assessment */}
              {capacityLoading ? (
                <Card className="p-8">
                  <LoadingSpinner />
                </Card>
              ) : capacityData ? (
                <Card className="p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Capacity Assessment
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Total Capacity</div>
                      <div className="text-lg font-bold">{capacityData.capacity.total_hours_per_week}h/week</div>
                      <div className="text-xs text-gray-500">({capacityData.capacity.total_fte} FTE)</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Assigned</div>
                      <div className="text-lg font-bold">{capacityData.capacity.assigned_hours_per_week.toFixed(1)}h/week</div>
                      <div className="text-xs text-gray-500">
                        ({((capacityData.capacity.assigned_hours_per_week / capacityData.capacity.total_hours_per_week) * 100).toFixed(1)}%)
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Available</div>
                      <div className={`text-lg font-bold ${
                        capacityData.capacity.available_hours_per_week < 10 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {capacityData.capacity.available_hours_per_week.toFixed(1)}h/week
                      </div>
                      <div className="text-xs text-gray-500">
                        ({capacityData.capacity.available_fte.toFixed(2)} FTE)
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Utilization</div>
                      <div className={`text-lg font-bold ${
                        capacityData.capacity.utilization_percent >= 90 ? 'text-red-600' : 
                        capacityData.capacity.utilization_percent >= 75 ? 'text-yellow-600' : 
                        'text-green-600'
                      }`}>
                        {capacityData.capacity.utilization_percent.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  {capacityData.warnings.capacity_exceeded && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                        <XCircle className="w-5 h-5" />
                        <span className="font-semibold">Capacity Exceeded</span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                        Staff is over-allocated ({capacityData.capacity.utilization_percent.toFixed(1)}% utilization)
                      </p>
                    </div>
                  )}

                  {capacityData.warnings.capacity_warning && !capacityData.warnings.capacity_exceeded && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">Capacity Warning</span>
                      </div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                        Staff is {capacityData.capacity.utilization_percent.toFixed(1)}% allocated (approaching capacity)
                      </p>
                    </div>
                  )}

                  {/* Current Projects */}
                  {capacityData.current_projects.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Current Projects ({capacityData.current_projects.length})</h4>
                      <div className="space-y-2">
                        {capacityData.current_projects.map((project, idx) => (
                          <div key={(project as any).report_id || idx} className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                            <div className="font-medium">{(project as any).report_name || (project as any).project_type || 'Unknown Project'}</div>
                            {project.service_line && (
                              <div className="text-xs text-gray-500">{project.service_line}</div>
                            )}
                            {project.role && (
                              <div className="text-xs text-gray-500">Role: {project.role}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upcoming Deadlines */}
                  {capacityData.upcoming_deadlines.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Upcoming Deadlines ({capacityData.upcoming_deadlines.length})</h4>
                      <div className="space-y-2">
                        {capacityData.upcoming_deadlines.slice(0, 5).map((deadline, idx) => (
                          <div key={idx} className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm">
                            <div className="font-medium">{deadline.title}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(deadline.deadline).toLocaleDateString()} ({deadline.days_until} days)
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
